import { animate, sequence, state, style, transition, trigger } from '@angular/animations';
import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { AccessibleNavigationOption } from './segmented-control.interface';

export enum EnumCustomWidth {
  DEFAULT = 'Default',
  SMALL = 'Small',
  FULL_WIDTH = 'full',
}
@Component({
  selector: 'vm-segmented-control',
  templateUrl: './segmented-control.component.html',
  styleUrls: ['./segmented-control.component.scss'],
  animations: [
    trigger('selectedOption', [
      state('default', style({ opacity: 0 })),
      state('first', style({ opacity: 1, transform: 'translateX(0%)' })),
      state('second', style({ opacity: 1, transform: 'translateX(100%)' })),
      state('third', style({ opacity: 1, transform: 'translateX(200%)' })),
      state('first-init', style({ opacity: 1, transform: 'translateX(0%)' })),
      state('second-init', style({ opacity: 1, transform: 'translateX(100%)' })),
      state('third-init', style({ opacity: 1, transform: 'translateX(200%)' })),
      transition('* => first-init', [
        sequence([style({ opacity: 0, transform: 'translateX(0%)' }), animate('0.3s ease')]),
      ]),
      transition('* => second-init', [
        sequence([style({ opacity: 0, transform: 'translateX(100%)' }), animate('0.3s ease')]),
      ]),
      transition('* => third-init', [
        sequence([style({ opacity: 0, transform: 'translateX(200%)' }), animate('0.3s ease')]),
      ]),
      transition('* => *', animate('0.3s ease-in-out')),
    ]),
  ],
})
export class SegmentedControlComponent implements OnInit {
  @Input() customWidth: EnumCustomWidth = EnumCustomWidth.DEFAULT;
  @Input() primaryColor = true;
  @Input() options: string[] = [];
  @Input() selectedOption = -1;
  @Input() isError = false;
  @Output() public optionSelected: EventEmitter<string> = new EventEmitter();
  focusedOption = -1;
  private valid = false;
  focused: boolean;
  hovered: boolean;
  selectedOptionString = 'default';

  ngOnInit(): void {
    this.checkValidity();
    if (this.activeSelection()) {
      this.clickedTab(this.selectedOption);
    }
  }

  clickedTab(tabIndex: number): void {
    this.selectedOption = tabIndex;
    switch (this.selectedOption) {
      case 0: {
        this.selectedOptionString = this.selectedOptionString === 'default' ? 'first-init' : 'first';
        break;
      }
      case 1: {
        this.selectedOptionString = this.selectedOptionString === 'default' ? 'second-init' : 'second';
        break;
      }
      case 2: {
        this.selectedOptionString = this.selectedOptionString === 'default' ? 'third-init' : 'third';
        break;
      }
      default: {
        break;
      }
    }
    this.optionSelected.emit(this.options[this.selectedOption]);
  }

  isCurrentTab(tabIndex: number): boolean {
    return this.selectedOption === tabIndex;
  }

  activeSelection(): boolean {
    return this.selectedOption !== -1;
  }

  private checkValidity(): boolean {
    return (this.valid = this.options.length <= 1 || this.options.length >= 4);
  }

  input(event: any, tabIndex: number): void {
    switch (event.code) {
      case 'ArrowLeft':
      case 'ArrowRight': {
        const keyInput: AccessibleNavigationOption = event.code as AccessibleNavigationOption;
        const next = this.getNextSelection(keyInput);
        this.focusedOption = -1;
        this.clickedTab(next);
        break;
      }
      case 'Tab': {
        this.focusedOption = tabIndex;
        break;
      }
    }
  }

  private getNextSelection(direction: AccessibleNavigationOption): number {
    if (this.focusedOption !== -1) {
      return this.focusedOption;
    }
    switch (direction) {
      case AccessibleNavigationOption.LEFT: {
        if (this.selectedOption <= 0) {
          return this.options.length - 1;
        } else {
          return this.selectedOption - 1;
        }
      }
      case AccessibleNavigationOption.RIGHT:
        if (this.selectedOption >= this.options.length - 1) {
          return 0;
        } else {
          return this.selectedOption + 1;
        }
    }
  }
}
